/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

//react imports
import React, { ErrorInfo } from 'react';

//ReactNativeErrorBoundary imports
import { ErrorBoundaryProps, ErrorBoundaryState } from './errorBoundary.types';
import FallbackComponent from '../FallbackComponent';
import {
  log_analytics,
  log_crashlytics,
} from '@airasia-rn/error-boundary/src/analytics';
import { Logger, LoggerContext } from '@airasia-rn/core-logger';
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  static contextType = LoggerContext;

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    //callback ran to expose to the parent the functionality
    if (typeof this.props.onError === 'function') {
      this.props.onError?.(error, errorInfo.componentStack ?? '');
      return;
    }

    log_analytics?.(
      this.props.lobName ?? '',
      this.props.screenName ?? '',
      error,
    );
    log_crashlytics?.(error, this.props.lobName ?? '');

    (this.context as Logger).logError(error, {
      lob: this.props.lobName ?? '',
      screenName: this.props.screenName ?? '',
    });
  }

  render() {
    if (this.state.hasError) {
      //Rendering custom fallback
      return (
        <FallbackComponent
          buttonLabel={this.props.buttonLabel ?? 'BUTTON_RETRY_TEXT'}
          onButtonPress={this.props.onButtonPress}
          locale={this.props.locale ?? 'en-gb'}
          retryLabel={this.props.retryLabel}

          //if no locale passed then use en-gb as locale
        />
      );
    }
    //else render the component
    return this.props.children;
  }
}

export default ErrorBoundary;
